import { Modal } from 'antd/lib';
import Button from 'antd/lib/button';
import Row from 'antd/lib/row';
import Spin from 'antd/lib/spin';
import Typography from 'antd/lib/typography';
import React, { useState } from 'react';
import { useEmailUtilityAutomationHistoryContentQuery } from '../../domain/email-utility-automation/queries';
import { wrapEmailHtmlForPreview } from '../../domain/email-utility-automation/wrapEmailHtmlForPreview';

interface IEmailUtilityAutomationHistoryContentModalProps {
    messageId: string;
    onClose: () => void;
    /** Set to false when opening on top of another modal - stacked masks compound to ~70% black. */
    mask?: boolean;
}

// Fixed so the body keeps its size across loading -> loaded and the modal doesn't jump once the email arrives.
const PREVIEW_HEIGHT = '67vh';

export const EmailUtilityAutomationHistoryContentModal: React.FC<IEmailUtilityAutomationHistoryContentModalProps> = ({
    messageId,
    onClose,
    mask = true,
}) => {
    const { data, isLoading, isError, error } = useEmailUtilityAutomationHistoryContentQuery(messageId);
    const [isFrameLoaded, setIsFrameLoaded] = useState(false);

    // Emails run to tens of kilobytes of markup, so the iframe needs a moment to paint after the fetch resolves.
    // Keeping the spinner up until its load event avoids swapping the spinner for a blank frame.
    const isPreviewPending = isLoading || (!!data && !isFrameLoaded);

    return (
        <Modal
            open
            width={900}
            title={data?.subject || 'Email Preview'}
            destroyOnHidden
            mask={mask}
            onCancel={onClose}
            style={{ top: 50 }}
            styles={{ header: { paddingRight: 42 } }} // To make sure, long header title is not overflowed by the close icon
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Close
                </Button>,
            ]}
        >
            {isError ? (
                <Row justify="center" style={{ width: '100%', padding: '8px 0' }}>
                    <Typography.Text type="danger">{error?.message || 'An Error occurred!'}</Typography.Text>
                </Row>
            ) : (
                <>
                    {/* Height is reserved while loading so the preview below does not shift once the sender resolves. */}
                    <div style={{ minHeight: 20, marginBottom: 8 }}>
                        {data && (
                            <Typography.Text
                                type="secondary"
                                style={{ fontSize: 12, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                                From: {data.from}
                            </Typography.Text>
                        )}
                    </div>
                    <div style={{ position: 'relative', height: PREVIEW_HEIGHT }}>
                        {data && (
                            <iframe
                                title={data.subject}
                                srcDoc={wrapEmailHtmlForPreview(data.html)}
                                sandbox=""
                                onLoad={() => setIsFrameLoaded(true)}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    border: '1px solid #d9d9d9',
                                    visibility: isFrameLoaded ? 'visible' : 'hidden',
                                }}
                            />
                        )}
                        {isPreviewPending && (
                            <Row justify="center" align="middle" style={{ position: 'absolute', inset: 0 }}>
                                <Spin size="large" />
                            </Row>
                        )}
                    </div>
                </>
            )}
        </Modal>
    );
};
